import type { EffortStop } from '@parsleyai/ui'
import type { Timestamp } from 'firebase/firestore'
import type { Budget } from './mealPlan'

export interface PlateComponent {
  category: 'vegetable' | 'protein' | 'carb' | 'fat' | 'fruit' | 'dairy' | 'legume'
  parts: number
  notes?: string
}

export interface CarbPreferences {
  preferred: string[]
  avoided: string[]
}

export interface PlateShape {
  components: PlateComponent[]
  carbPreferences?: CarbPreferences | null
}

// Mirrors the server enum in packages/functions; the string values are the on-the-wire contract.
export enum CuisineStance {
  Liked = 'liked',
  Disliked = 'disliked'
}

/** Cuisine id → stance. A missing key is no opinion, so the map stays sparse. */
export type CuisinePreferences = Partial<Record<string, CuisineStance>>

/** Which way of declaring effort is in effect. Both ways' values are kept. */
export enum EffortMode {
  ByDay = 'day',
  ByCount = 'count'
}

export enum DayOfWeek {
  Mon = 'mon',
  Tue = 'tue',
  Wed = 'wed',
  Thu = 'thu',
  Fri = 'fri',
  Sat = 'sat',
  Sun = 'sun'
}

/** A level that names an effort — every stop but Any. The engine's `EffortBucket` uses the same strings. */
export type EffortLevel = Exclude<EffortStop, EffortStop.Any>

/** Days that differ from the usual level. A day belongs to at most one rule. */
export interface EffortRule {
  id: string
  days: DayOfWeek[]
  /** Null on a new row until a level is picked; such a row isn't saved. */
  level: EffortLevel | null
}

export interface EffortPreferences {
  mode: EffortMode
  /** The usual level (Any leaves it to Parsley), with rules over it. */
  days: { base: EffortStop; rules: EffortRule[] }
  /** Dinners a week at each level; seven at most between them. */
  counts: Record<EffortLevel, number>
}

export interface NutritionTargets {
  calorieMin?: number | null
  calorieMax?: number | null
  proteinGrams?: number | null
  carbGrams?: number | null
  fatGrams?: number | null
  fiberMinGrams?: number | null
}

// Mirrors the server enum in packages/functions; the string values are the on-the-wire contract.
export enum FrequencyGroup {
  RedMeat = 'red-meat',
  Fish = 'fish',
  Poultry = 'poultry',
  PlantBased = 'plant-based'
}

/** Dinners per week; `null` on a side means no bound there. */
export interface FrequencyLimit {
  group: FrequencyGroup
  minServings: number | null
  maxServings: number | null
}

// Lifecycle of the LLM-generated eating-style summary. Mirrors the
// server enum in packages/functions; the string values are the on-the-wire
// contract, so keep them in sync.
export enum SummaryStatus {
  Generating = 'generating',
  Ready = 'ready',
  Failed = 'failed',
  Stale = 'stale'
}

export interface EatingPreferences {
  plateShape: PlateShape | null
  cuisinePreferences: CuisinePreferences | null
  effort: EffortPreferences | null
  nutritionTargets: NutritionTargets | null
  // Absent on docs written before the field existed.
  frequencyLimits?: FrequencyLimit[] | null
  // The usual grocery budget. Absent or null means none saved, which plans treat as Normal.
  budget?: Budget | null
  createdAt?: Timestamp
  updatedAt?: Timestamp
  // LLM eating-style summary. Server-only — written by the
  // onEatingStyleWritten enrichment trigger, never by the client. The hero
  // shows `summaryText` when `summaryStatus === Ready` and falls back to the
  // deterministic `generateEatingStyleSummary` while generating / failed / absent.
  summaryText?: string | null
  summaryStatus?: SummaryStatus
  summaryGeneratedAt?: number
  summarySourceHash?: string
  // Handle for the artifact record behind `summaryText` — what a rating attaches
  // to. Absent when there is no summary to rate.
  summaryGenerationId?: string
}
